import { Field } from 'formik';
import { useEffect, useMemo, useRef } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { FormStepHeader } from '../../../../../components/forms';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import { useGetAllScholarshipsQuery } from '../../../../../services/endpoints/orders/scholarship';
import { useGetAllParentsQuery, useGetParentQuery } from '../../../../../services/endpoints/people/parent';
import { Child, User } from '../../../../../services/endpoints/people/user';
import { useGetCoursesSimpleQuery } from '../../../../../services/endpoints/schedule/course';
import { Scholarship } from '../../../../../types/orders.type';
import { scholarshipTypes } from '../../../../../utils/consts/orders/scholarships';
import { StepProps } from './AddEditDiscount';

const parentDropdownQuery = {
  scope: 'active_parents',
  data: { entries: 500000, orderBy: { field: 'first_name', order: 'asc' } }
};

export const DiscountForm = ({ touched, errors, values, setFieldValue }: StepProps) => {
  const selectedParentId = values?.user_id?.value;
  const prevParentIdRef = useRef<number | undefined>();

  const { data: parentData, error: parentError } = useGetAllParentsQuery(parentDropdownQuery);
  const { data: selectedParentData, error: selectedParentChildrenError } = useGetParentQuery(
    Number(selectedParentId),
    { skip: !selectedParentId }
  );
  const { data: scholarshipData, error: scholarshipError } = useGetAllScholarshipsQuery({
    data: { entries: 1000 }
  });
  const { data: courseData, error: courseError } = useGetCoursesSimpleQuery({
    scope: 'upcoming',
    data: {
      entries: 10000,
      orderBy: { field: 'title', order: 'asc' }
    }
  });

  const childOptions = useMemo(
    () =>
      selectedParentData?.parent?.children?.map((child: Child) => ({
        value: child.id!,
        label: child.full_name ?? `${child.first_name} ${child.last_name}`
      })) ?? [],
    [selectedParentData]
  );

  useEffect(() => {
    const prevParentId = prevParentIdRef.current;
    prevParentIdRef.current = selectedParentId;

    if (setFieldValue && prevParentId !== undefined && prevParentId !== selectedParentId) {
      setFieldValue('child_id', null);
    }
  }, [selectedParentId, setFieldValue]);

  return (
    <Row>
      <Col xs={12}>
        <FormStepHeader title="Discount Details" />
      </Col>
      <Col
        xs={12}
        lg={6}
        className="pe-1"
      >
        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="user_id"
        >
          <Field
            as={FormSelect}
            name="user_id"
            label="User"
            required
            options={
              parentData?.results?.map((user: User) => ({
                value: user.id,
                label: `${user.first_name} ${user.last_name} (${user.email})`
              })) ?? []
            }
            loadingError={parentError ? 'Error loading users' : undefined}
          />
          <Form.Text>
            Enter first name, last name, or email address to find the relevant user.
          </Form.Text>
        </Form.Group>

        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="scholarship_id"
        >
          <Field
            as={FormSelect}
            name="scholarship_id"
            label="Scholarship"
            required
            options={
              scholarshipData?.results?.map((item: Scholarship) => ({
                value: item.id,
                label: `${scholarshipTypes[item.scholarship_type]} (${item.percent_discount}% off)`
              })) ?? []
            }
            loadingError={scholarshipError ? 'Error loading scholarships' : undefined}
          />
        </Form.Group>

        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="course_id"
        >
          <Field
            as={FormSelect}
            name="course_id"
            label="Upcoming Session"
            required
            options={
              courseData?.results?.map((course: { id: number; title: string }) => ({
                value: course.id,
                label: course.title
              })) ?? []
            }
            loadingError={courseError ? 'Error loading sessions' : undefined}
          />
        </Form.Group>

        <Form.Group
          className="mb-lg-3 mb-1"
          controlId="child_id"
        >
          <Field
            as={FormSelect}
            name="child_id"
            label="Child"
            disabled={!selectedParentId}
            options={childOptions}
            loadingError={selectedParentChildrenError ? 'Error loading children' : undefined}
          />
          {!selectedParentId && (
            <Form.Text>Select a user first to choose a child.</Form.Text>
          )}
          {selectedParentId && childOptions.length === 0 && !selectedParentChildrenError && (
            <Form.Text className="text-muted">
              No children found for this user.
            </Form.Text>
          )}
        </Form.Group>
      </Col>
    </Row>
  );
};
